import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  Image,
  Text
} from "@chakra-ui/react"
import { useMediaQuery } from "@uidotdev/usehooks"
import { Fragment, useMemo } from "react"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"
import madeInAus from "../../assets/images/madeInAus.png"
import { Paths, URLForwardSlashRestore, mobileBreakpoint } from "../../common/commonConstants"
import {
  useGetInstallationInstructionReportLazyQuery,
  useGetProductDrawingsQuery,
  useGetProductInstructionAssignmentsQuery,
  useProductByCodeQuery
} from "../../type-and-hooks"
import { type ResolvedFile, FileResourceRow } from "../commons/FileResourceRow"
import { NewFileLinkButton, PDFLinkButton } from "../commons/PDFLinkButton"
import { ErrorMessage } from "../commons/error.main"
import { Loading } from "../commons/loader.main"
import { PDFIconImg, revitIconImg } from "../images"
import { ProductActionButtons } from "./product-info.action-buttons"

export const PDFIcon = (): JSX.Element => <Image src={PDFIconImg} />
export const REVITIcon = (): JSX.Element => <Image src={revitIconImg} />

interface ProductInfoProps {
  productCode?: string
}

const HYPERLINK_PATTERN = /\[hyperlinked to (https?:\/\/[^\]]+)\]/g

function renderDescriptionWithLinks(text: string): JSX.Element {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  HYPERLINK_PATTERN.lastIndex = 0
  while ((match = HYPERLINK_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<Fragment key={lastIndex}>{text.slice(lastIndex, match.index)}</Fragment>)
    }
    parts.push(
      <a key={match.index} href={match[1]} target="_blank" rel="noreferrer" style={{ color: '#C1440E', textDecoration: 'underline' }}>
        clicking here
      </a>
    )
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    parts.push(<Fragment key={lastIndex}>{text.slice(lastIndex)}</Fragment>)
  }
  return <>{parts}</>
}

export const ProductInfo = ({ productCode }: ProductInfoProps): JSX.Element => {
  const { id: productId } = useParams<{ id: string }>()
  // Use the prop if provided, otherwise fall back to URL parameter
  const id = productCode ?? URLForwardSlashRestore(productId)

  const isMS70C = id?.toUpperCase() === "MS70C"
  const isMS70R = id?.toUpperCase() === "MS70R"
  const isUtilityCollar = id?.toUpperCase() === "UTILITY-COLLAR"

  const isStaticProductPage = isMS70C || isMS70R || isUtilityCollar

  const { loading, error, data } = useProductByCodeQuery({ variables: { productCode: id ?? "" }, fetchPolicy: "cache-and-network" })
  const { data: drawingsData } = useGetProductDrawingsQuery({ fetchPolicy: "cache-and-network" })
  const { data: instructionsData } = useGetProductInstructionAssignmentsQuery({ fetchPolicy: "cache-and-network" })
  const navigate = useNavigate()
  const isMobile = useMediaQuery(`(max-width:${mobileBreakpoint}px)`)

  const drawingUrls = useMemo(() => {
    const map: Record<
      string,
      {
        pdf_url?: string | null
        revit_url?: string | null
        pdf_file_name?: string | null
        revit_file_name?: string | null
        pdf_download_url?: string | null
      }
    > = {}
    drawingsData?.getProductDrawings?.forEach(d => {
      if (d?.product_code) {
        map[d.product_code] = {
          pdf_url: d.pdf_url,
          revit_url: d.revit_url,
          pdf_file_name: d.pdf_file_name,
          revit_file_name: d.revit_file_name,
          pdf_download_url: d.pdf_download_url
        }
      }
    })
    return map
  }, [drawingsData])

  const drawingEntry = drawingUrls[id?.toUpperCase() ?? ""] ?? {}
  const drawingPdfUrl = drawingEntry.pdf_url ?? undefined
  const drawingPdfFileName = drawingEntry.pdf_file_name ?? undefined
  const drawingPdfDownloadUrl = drawingEntry.pdf_download_url ?? undefined
  const revitUrl = drawingEntry.revit_url ?? undefined
  const revitFileName = drawingEntry.revit_file_name ?? undefined

  const instructionUrls = useMemo(() => {
    const map: Record<string, { instruction_no?: string | null; instruction_desc?: string | null }> = {}
    instructionsData?.getProductInstructionAssignments?.forEach(pi => {
      if (pi?.product_code) map[pi.product_code] = { instruction_no: pi.instruction_no, instruction_desc: pi.instruction_desc }
    })
    return map
  }, [instructionsData])

  const instructionEntry = instructionUrls[id?.toUpperCase() ?? ""]

  const downloadRevit = (url: string, filename: string): void => {
    const link = document.createElement("a")
    link.href = url
    link.download = `${filename}.rfa`
    link.target = "_blank"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const [getInstallationReport] = useGetInstallationInstructionReportLazyQuery({ fetchPolicy: "network-only" })

  const resolveInstructionFile = async (): Promise<ResolvedFile | null> => {
    if (!instructionEntry?.instruction_no) return null
    const { data: reportData } = await getInstallationReport({ variables: { instruction: instructionEntry.instruction_no } })
    const url = reportData?.getInstallationInstructionReport?.reportURL ?? null
    const downloadUrl = reportData?.getInstallationInstructionReport?.downloadURL ?? null
    const fileName = reportData?.getInstallationInstructionReport?.fileName ?? null
    return url ? { url, downloadUrl, fileName } : null
  }

  const installationInstructionsButton = instructionEntry?.instruction_no ? (
    <FileResourceRow
      label={instructionEntry.instruction_desc ?? "Installation Instructions"}
      mode="pdf"
      resolveFile={resolveInstructionFile}
    />
  ) : (
    <PDFLinkButton text="Installation Instructions" props={{ onClick: () => { void navigate(Paths.INSTALLATION_INSTRUCTIONS) } }} />
  )

  const drawingPdfButton = drawingPdfUrl && (
    <FileResourceRow
      label="Drawing PDF"
      mode="pdf"
      resolveFile={() =>
        Promise.resolve(
          drawingPdfUrl ? { url: drawingPdfUrl, downloadUrl: drawingPdfDownloadUrl, fileName: drawingPdfFileName } : null
        )
      }
    />
  )

  const revitDrawingButton = revitUrl && (
    <FileResourceRow
      label="Revit File"
      mode="revit"
      resolveFile={() => Promise.resolve(revitUrl ? { url: revitUrl, fileName: revitFileName } : null)}
    />
  )

  return (
    <>
      <Loading loading={loading} />
      {error && <ErrorMessage error={"Failed to load: Product Info."} primaryAction={false} primaryActionText={""} />}
      {data && (
        <Box
          color="#000"
          width="100%"
          px={["16px", "16px", "16px", "48px", "64px", "80px"]}
          maxW={["100%", "100%", "100%", "100%"]}
          mb="32px"
          display="flex"
          flexDir="column"
        >
          <Flex justify={["space-between", "space-between", "initial"]}>
            <Box flexShrink={"10"}>
              <Heading as="h1" variant={"Heading1"} w="100%" fontWeight={"500"} wordBreak="break-all">
                {data?.productByCode?.product_code}
              </Heading>
              <Heading mt="2" variant={"RHeading3CAPS"} w="100%" textTransform={"uppercase"}>
                {data?.productByCode?.installation_type}
              </Heading>
              {isMobile && (
                <Accordion allowToggle>
                  <AccordionItem border="0">
                    <AccordionButton p="0" border="0" mt="20px" alignItems="center" display="flex" justifyContent="flex-end" flexDir="row-reverse">
                      <Heading fontWeight={"400"} fontSize="14px">
                        Product Description
                      </Heading>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel>
                      <Text fontSize={"15px"} whiteSpace="pre-wrap" textAlign="left" pb="0">
                        {data?.productByCode?.product_desc ? renderDescriptionWithLinks(data.productByCode.product_desc) : null}
                      </Text>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )}
              {!isMobile && (
                <Text mt="30px" fontSize={"15px"} whiteSpace="pre-wrap" textAlign="left">
                  {data?.productByCode?.product_desc ? renderDescriptionWithLinks(data.productByCode.product_desc) : null}
                </Text>
              )}
            </Box>
            <Box w="68px" pt="4">
              <Image w="68px" src={madeInAus} />
            </Box>
          </Flex>
          {isMobile && (
            <Accordion allowToggle>
              <AccordionItem border="0">
                <AccordionButton p="0" border="0" mt="16px" alignItems="center" display="flex" justifyContent="flex-end" flexDir="row-reverse">
                  <Heading fontWeight={"400"} fontSize="22px">
                    Related Resources:
                  </Heading>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <Flex pt="2" flexDir="column" align="flex-start" gap="1">
                    {isMS70C && <PDFLinkButton text="Report FCO3481" reportNo="FCO3481" />}
                    {isMS70R && <PDFLinkButton text="Report FCO3397" reportNo="FCO3397" />}
                    {isUtilityCollar && <PDFLinkButton text="Report FCO3520" reportNo="FCO3520" />}
                    {isUtilityCollar && <PDFLinkButton text="Data Sheet" installationReport="INS-UCR-DS" />}
                    {isMS70C && <PDFLinkButton text="Installation Instruction" installationReport="INS-MSCI" />}
                    {isMS70R && <PDFLinkButton text="Installation Instruction" installationReport="INS-MSR" />}
                    {isUtilityCollar && <PDFLinkButton text="Installation Instruction" installationReport="INS-UCR" />}
                    {!isStaticProductPage && !isUtilityCollar && installationInstructionsButton}
                    {isStaticProductPage && <PDFLinkButton text="Drawing PDF" url={drawingPdfUrl} fileName={drawingPdfFileName} />}
                    {isStaticProductPage && revitUrl && <NewFileLinkButton text="Revit File" props={{ onClick: () => { downloadRevit(revitUrl, id ?? "revit") } }} />}
                    {!isStaticProductPage && drawingPdfButton}
                    {!isStaticProductPage && revitDrawingButton}
                  </Flex>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}
          {!isMobile && (
            <>
              <Heading fontWeight={"400"} fontSize="22px" mt="20px">
                Related Resources:
              </Heading>
              <Flex pt="2" flexDir="column" align="flex-start" gap="1">
                {isMS70C && <PDFLinkButton text="Report FCO3481" reportNo="FCO3481" />}
                {isMS70R && <PDFLinkButton text="Report FCO3397" reportNo="FCO3397" />}
                {isUtilityCollar && <PDFLinkButton text="Report FCO3520" reportNo="FCO3520" />}
                {isUtilityCollar && <PDFLinkButton text="Data Sheet" installationReport="INS-UCR-DS" />}
                {isMS70C && <PDFLinkButton text="Installation Instruction" installationReport="INS-MSCI" />}
                {isMS70R && <PDFLinkButton text="Installation Instruction" installationReport="INS-MSR" />}
                {isUtilityCollar && <PDFLinkButton text="Installation Instruction" installationReport="INS-UCR" />}
                {!isStaticProductPage && installationInstructionsButton}
                {isStaticProductPage && <PDFLinkButton text="Drawing PDF" url={drawingPdfUrl} fileName={drawingPdfFileName} />}
                {isStaticProductPage && <NewFileLinkButton text="Revit File" />}
                {!isStaticProductPage && drawingPdfButton}
                {!isStaticProductPage && revitDrawingButton}
              </Flex>
            </>
          )}
          <Box mt="auto" pt="24px">
            <ProductActionButtons />
          </Box>
        </Box>
      )}
    </>
  )
}
